import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { StepHandle, StepProps } from '../BusinessProfileSetupPage';
import { businessProfileSetupGateway } from '../../services/BusinessProfileSetupGateway';
import { feinDigitsFromInput, formatFeinDisplay, formatUsPhoneDisplay, normalizeWebsiteUrl, usPhoneDigitsFromInput } from '../../../../shared/formatting';
import { validEmail, validFein, validSein, validWebsite } from '../../validation';
import { ApiError } from '../../../identity/types';
import { CUSTOMER_CARE_PHONE } from '../../supportContact';

interface FormState {
  name: string;
  fein: string;
  email: string;
  phone: string;
  sein: string;
  website: string;
}

const EMPTY: FormState = { name: '', fein: '', email: '', phone: '', sein: '', website: '' };

function validate(form: FormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (form.name.trim().length < 1) errors.name = 'Company Name is required.';
  else if (form.name.trim().length > 100) errors.name = 'Company Name must be 100 characters or fewer.';
  if (!validFein(form.fein)) errors.fein = 'Invalid FEIN. Must contain 9 digits in XX-XXXXXXX format.';
  if (!validSein(form.sein)) errors.sein = 'Use the state-issued employer ID format; letters, numbers, spaces, ., #, / and - are accepted.';
  if (!form.email.trim()) errors.email = 'Company Email is required.';
  else if (!validEmail(form.email)) errors.email = 'Enter a valid email address, e.g. name@company.com.';
  if (!form.phone) errors.phone = 'Company Phone Number is required.';
  else if (form.phone.replace(/\D/g, '').length < 10) errors.phone = 'Enter a 10-digit US number or an international number with +country code.';
  if (!validWebsite(form.website)) errors.website = 'Enter a valid website, e.g. https://company.com.';
  return errors;
}

export const BasicInfoStep = forwardRef<StepHandle, StepProps>(function BasicInfoStep({ markStepComplete, profile, readOnly }, ref) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  // Set when the server reports that another active company already holds this FEIN (code FEIN_ALREADY_REGISTERED); shown as a banner at the top of the step.
  const [duplicateFein, setDuplicateFein] = useState<string | null>(null);
  // EPN-37 unsaved-changes correction: the form state loaded on mount, captured once and never updated
  // afterward — isDirty (below) compares the CURRENT form against this baseline. A ref, not state: reading
  // it must never itself trigger a re-render.
  const baselineRef = useRef<FormState>(EMPTY);

  useEffect(() => {
    const info = profile?.basicInfo;
    if (!info) return;
    const draft = info.draft;
    const loaded: FormState = {
      name: draft?.name ?? info.name ?? '',
      fein: draft?.fein ?? info.fein ?? '',
      email: draft?.email ?? info.email ?? '',
      phone: draft?.phone ?? info.phone ?? '',
      sein: draft?.sein ?? info.sein ?? '',
      website: draft?.website ?? info.website ?? '',
    };
    setForm(loaded);
    baselineRef.current = loaded;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (field: keyof FormState, value: string) => setForm((prev) => ({ ...prev, [field]: value }));

  const toDraftRequest = () => ({
    name: form.name || null,
    fein: form.fein || null,
    email: form.email || null,
    phone: form.phone || null,
    sein: form.sein || null,
    website: form.website || null,
  });

  /** Shared by saveAndContinue (setup mode) and commitEdits (admin-edit's Save Changes) — both validate and
   * write the same canonical record; only the caller decides what happens next (advance the wizard vs.
   * simply close Edit mode — see BusinessProfileSetupPage.tsx's own handlers). */
  const commit = async () => {
    const validationErrors = validate(form);
    setErrors(validationErrors);
    setShowErrors(true);
    if (Object.keys(validationErrors).length > 0) {
      throw new Error('validation failed');
    }

    setApiError(null);
    setDuplicateFein(null);
    try {
      await businessProfileSetupGateway.saveBasicInfoAndContinue({
        name: form.name.trim(),
        fein: form.fein,
        email: form.email.trim().toLowerCase(),
        phone: form.phone,
        sein: form.sein.trim() ? form.sein.trim().toUpperCase() : null,
        website: form.website.trim() ? normalizeWebsiteUrl(form.website) : null,
      });
      markStepComplete('basic-info');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'FEIN_ALREADY_REGISTERED') {
        setDuplicateFein(formatFeinDisplay(feinDigitsFromInput(form.fein)));
      } else {
        setApiError(err instanceof ApiError ? err.message : 'Could not save this step. Please try again.');
      }
      throw err;
    }
  };

  useImperativeHandle(ref, () => ({
    saveAndContinue: commit,
    async saveAndExit() {
      setApiError(null);
      try {
        await businessProfileSetupGateway.saveBasicInfoDraft(toDraftRequest());
      } catch (err) {
        setApiError(err instanceof ApiError ? err.message : 'Could not save your draft. Please try again.');
      }
    },
    commitEdits: commit,
    isDirty: () => JSON.stringify(form) !== JSON.stringify(baselineRef.current),
  }));

  const err = (field: string) => (showErrors ? errors[field] : undefined);

  return (
    <div>
      {duplicateFein && (
        <div className="bp-banner-error" role="alert" data-testid="fein-duplicate-banner">
          Conflict: A company with FEIN {duplicateFein} is already registered in EPN. Please contact Customer Care at {CUSTOMER_CARE_PHONE}.
        </div>
      )}
      <div className="bp-grid">
        <div className="bp-form-group">
          <label htmlFor="bps-companyName">
            Company Name <span className="req">*</span>
          </label>
          <input
            id="bps-companyName"
            className={`bp-input${err('name') ? ' invalid' : ''}`}
            value={form.name}
            maxLength={100}
            placeholder="e.g. Escochex Inc."
            disabled={readOnly}
            onChange={(e) => set('name', e.target.value)}
            onBlur={() => set('name', form.name.replace(/\s+/g, ' ').trim())}
          />
          {err('name') && <div className="error">{err('name')}</div>}
        </div>
        <div className="bp-form-group">
          <label htmlFor="bps-fein">
            FEIN <span className="req">*</span>
          </label>
          <input
            id="bps-fein"
            className={`bp-input${err('fein') || duplicateFein ? ' invalid' : ''}`}
            value={formatFeinDisplay(feinDigitsFromInput(form.fein))}
            maxLength={10}
            inputMode="numeric"
            placeholder="e.g. 12-3456789"
            disabled={readOnly}
            onChange={(e) => {
              set('fein', feinDigitsFromInput(e.target.value));
              setDuplicateFein(null);
            }}
          />
          {err('fein') && <div className="error">{err('fein')}</div>}
        </div>
        <div className="bp-form-group">
          <label htmlFor="bps-sein">
            SEIN
            <span
              className="bp-info"
              role="img"
              tabIndex={0}
              aria-label="State employer ID formats vary by state. You can complete this later, but it is required before state payroll processing."
              title="State employer ID formats vary by state. You can complete this later, but it is required before state payroll processing."
            >
              i
            </span>
          </label>
          <input
            id="bps-sein"
            className="bp-input"
            value={form.sein}
            maxLength={24}
            placeholder="State employer ID (format varies)"
            disabled={readOnly}
            onChange={(e) => set('sein', e.target.value)}
            onBlur={() => set('sein', form.sein.replace(/\s+/g, ' ').trim().toUpperCase())}
          />
          {err('sein') && <div className="error">{err('sein')}</div>}
          {!form.sein.trim() && (
            <div className="bp-soft-warning" role="status" data-testid="sein-pending-warning">
              SEIN pending. Must be completed prior to state payroll processing.
            </div>
          )}
        </div>
        <div className="bp-form-group">
          <label htmlFor="bps-companyEmail">
            Company Email <span className="req">*</span>
          </label>
          <input
            id="bps-companyEmail"
            className={`bp-input${err('email') ? ' invalid' : ''}`}
            type="email"
            value={form.email}
            maxLength={254}
            placeholder="e.g. payroll@company.com"
            disabled={readOnly}
            onChange={(e) => set('email', e.target.value.replace(/\s/g, ''))}
            onBlur={() => set('email', form.email.trim().toLowerCase())}
          />
          {err('email') && <div className="error">{err('email')}</div>}
        </div>
        <div className="bp-form-group">
          <label htmlFor="bps-companyPhone">
            Company Phone Number <span className="req">*</span>
          </label>
          <input
            id="bps-companyPhone"
            className={`bp-input${err('phone') ? ' invalid' : ''}`}
            value={formatUsPhoneDisplay(usPhoneDigitsFromInput(form.phone))}
            maxLength={22}
            inputMode="tel"
            placeholder="e.g. (818) 555-0134"
            disabled={readOnly}
            onChange={(e) => set('phone', usPhoneDigitsFromInput(e.target.value))}
          />
          {err('phone') && <div className="error">{err('phone')}</div>}
        </div>
        <div className="bp-form-group">
          <label htmlFor="bps-companyWebsite">Company Website</label>
          <input
            id="bps-companyWebsite"
            className={`bp-input${err('website') ? ' invalid' : ''}`}
            value={form.website}
            maxLength={200}
            inputMode="url"
            placeholder="e.g. https://company.com"
            disabled={readOnly}
            onChange={(e) => set('website', e.target.value)}
            onBlur={() => form.website.trim() && set('website', normalizeWebsiteUrl(form.website))}
          />
          {err('website') && <div className="error">{err('website')}</div>}
        </div>
      </div>

      {apiError && <div className="notice" style={{ color: 'var(--bps-danger)' }}>{apiError}</div>}
    </div>
  );
});
